/* eslint-disable no-unused-vars, react/prop-types, react-hooks/exhaustive-deps */
import React, { Suspense, useState, useEffect, useCallback } from "react";
import { Box, Grid2, CircularProgress } from "@mui/material";
import SupplierCard from "../../../Components/SupplierCards";
import SkeletonLoader from "../../../Components/SupplierCards/SkeletonLoader";
import SupplierProfileModal from "../../SupplierProfileModal";
import { apiGetRequest } from "../../../Helpers/Requests";
import endpoints from "../../../Helpers/Endpoints";
import { useTranslation } from "react-i18next";
import CommunitySuppliersHeaderFilters from "../../../Components/CommunitySuppliersHeaderFilters";
import { sortCategoriesWithSubcategories } from "../../../Utils/SortData";

function FindSupplier() {
  const delay = process.env.DEBOUNCE_DELAY;
  const [isLoading, setIsLoading] = useState(false);
  const [itemsToShow, setItemsToShow] = useState(50);
  const [activePage, setActivePage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [originalSuppliers, setOriginalSuppliers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [categories, setCategories] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showSupplierProfile, setShowSupplierProfile] = useState(false);
  const [supplierProfileData, setSupplierProfileData] = useState(null);
  const [refreshSuppliersData, setRefreshSuppliersData] = useState(false);

  const { t } = useTranslation("page-find-new-suppliers");

  const fetchSuppliers = async () => {
    try {
      const response = await apiGetRequest(
        endpoints.suppliers({
          page: activePage,
          pageSize: itemsToShow,
          searchValue: searchValue,
          categoryIds: selectedCategories,
          zoneIds: selectedLocations,
        })
      );
      setSuppliers(response?.data?.suppliers || []);
      setOriginalSuppliers(response?.data?.suppliers || []);
      setMaxPage(response?.data?.max_page_number ?? 1);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [suppliersResult, zonesResult, categoriesResult] =
        await Promise.allSettled([
          fetchSuppliers(),
          apiGetRequest(endpoints.getZones),
          apiGetRequest(endpoints.getAllCategories),
        ]);

      if (zonesResult.status === "fulfilled") {
        setZones(zonesResult.value.data || []);
      }

      if (categoriesResult.status === "fulfilled") {
        let categories = categoriesResult.value.data || [];

        categories = sortCategoriesWithSubcategories(categories);
        setCategories(categories);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSuppliers = async (pageNumb, isFilterChange = false) => {
    try {
      setIsLoading(true);
      //console.log('pagenumber', pageNumb ?? activePage);

      const response = await apiGetRequest(
        endpoints.suppliers({
          page: pageNumb ?? activePage,
          pageSize: itemsToShow,
          searchValue: searchValue,
          categoryIds: selectedCategories,
          zoneIds: selectedLocations,
        })
      );

      //setSuppliers(response?.data?.suppliers || []);
      setSuppliers((prevSuppliers) => {
        if (isFilterChange) {
          return response?.data?.suppliers || [];
        }
        const uniqueSuppliers = [
          ...prevSuppliers,
          ...response?.data?.suppliers,
        ].filter(
          (supplier, index, self) =>
            index === self.findIndex((s) => s.id === supplier.id)
        );
        return uniqueSuppliers;
      });

      setMaxPage(response?.data?.max_page_number ?? 1);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Infinite Scroll
  const handleScroll = useCallback(() => {
    if (isLoading) return;

    const scrollPosition = window.innerHeight + window.scrollY;
    const threshold = document.body.offsetHeight - 200;

    if (scrollPosition >= threshold && activePage < maxPage) {
      setActivePage((prev) => prev + 1);
    }
  }, [isLoading, activePage, maxPage]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setActivePage(1);
      getSuppliers(1, true);
    }, delay);

    return () => clearTimeout(delayDebounce);
  }, [searchValue, selectedCategories, selectedLocations]);

  useEffect(() => {
    getSuppliers(activePage);
  }, [activePage]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const clearFilters = () => {
    setSelectedCategories("");
    setSelectedLocations("");
    setSearchValue("");
  };

  const displaySupplierProfile = (item) => {
    console.log("opening profile", item);
    getSupplierProfileData(item?.id);
    setShowSupplierProfile(true);
  };

  const getSupplierProfileData = async (supplier_id) => {
    try {
      const response = await apiGetRequest(
        endpoints.getSupplierProfilePricelists(supplier_id)
      );
      setSupplierProfileData(response?.data || {});
    } catch (err) {
      console.log("error: ", err);
    }
  };

  const handleCloseSupplierProfileModal = () => {
    setShowSupplierProfile(false);
    setSupplierProfileData(null);
  };

  useEffect(() => {
    if (refreshSuppliersData) {
      setIsLoading(true);
      fetchSuppliers();
      setRefreshSuppliersData(false);
      setIsLoading(false);
    }
  }, [refreshSuppliersData]);

  const hasFilters =
    selectedLocations.length > 0 ||
    selectedCategories.length > 0 ||
    searchValue.trim() !== "";

  return (
    <>
      <Box>
        <Suspense>
          <CommunitySuppliersHeaderFilters
            {...{
              searchValue,
              setSearchValue,
              originalSuppliers,
              setSuppliers,
              clearFilters,
              selectedCategories,
              setSelectedCategories,
              selectedLocations,
              setSelectedLocations,
              categories,
              zones,
              hasFilters,
              showMobileFilters: false,
            }}
          />
        </Suspense>

        <Grid2
          container
          columnSpacing={3}
          rowSpacing={2}
          id="supplier-container"
        >
          {isLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <Grid2
                size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4, "2xl": 3 }}
                key={index}
                sx={{ display: "flex", flexWrap: "wrap" }}
              >
                <SkeletonLoader />
              </Grid2>
            ))
          ) : !suppliers || !Array.isArray(suppliers) ? (
            <p>{t("no_suppliers")}</p>
          ) : (
            suppliers.map((supplier, index) => (
              <Grid2
                size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4, "2xl": 3 }}
                key={index}
                sx={{ display: "flex", flexWrap: "wrap" }}
              >
                <SupplierCard
                  supplier={supplier}
                  displaySupplierProfile={displaySupplierProfile}
                  buttonText={t("supplier_card_button_text")}
                />
              </Grid2>
            ))
          )}
          {isLoading && <CircularProgress />}
        </Grid2>
      </Box>

      {showSupplierProfile && supplierProfileData && (
        <SupplierProfileModal
          {...{
            viewType:
              supplierProfileData.is_invited === true ? "view" : "invite",
            showDiscountBadge: false,
            openSupplierProfileModal: showSupplierProfile,
            supplierProfileData,
            handleCloseSupplierProfileModal,
            setRefreshSuppliersData,
          }}
        />
      )}
    </>
  );
}

export default FindSupplier;
